import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, RefreshCw, Trash2, UserPlus } from 'lucide-react';
import type { InviteGrantInput, OrgInviteDto, OrgMemberDto, ProjectRole } from '@aiai/shared';
import {
  changeOrgMemberRole,
  fetchOrgMembers,
  inviteOrgMember,
  removeOrgMember,
  resendOrgInvite,
  revokeOrgInvite,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { useAuth } from '../auth/useAuth';
import { useOrg } from '../org/useOrg';
import { Badge } from '../components/Badge';
import { Callout } from '../components/Callout';
import { FormField } from '../components/FormField';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import {
  GrantSelect,
  MemberGrantsEditor,
  type GrantSelectValue,
} from '../components/MemberGrantsEditor';
import { useWorkspaceProjectOptions } from '../hooks/useWorkspaceProjectOptions';
import { LoadingState, ErrorState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function RoleBadge({ role }: { role: 'owner' | 'member' }) {
  return (
    <Badge tone={role === 'owner' ? 'warning' : 'neutral'} size="sm">
      {role}
    </Badge>
  );
}

function MemberRow({
  member,
  canManage,
  currentUserId,
  orgId,
  isLastOwner,
}: {
  member: OrgMemberDto;
  canManage: boolean;
  currentUserId: string;
  orgId: string;
  /** This member is the workspace's only owner — they can't be demoted. */
  isLastOwner: boolean;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [confirmingRole, setConfirmingRole] = useState(false);
  // Project access is off-screen by default — most workspaces are small and
  // rarely need per-project overrides, so keep the row compact until asked.
  const [grantsOpen, setGrantsOpen] = useState(false);

  const remove = useMutation({
    mutationFn: () => removeOrgMember(orgId, member.userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) }),
  });

  const nextRole = member.role === 'owner' ? 'member' : 'owner';
  const changeRole = useMutation({
    mutationFn: () => changeOrgMemberRole(orgId, member.userId, nextRole),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) });
      // Your own role drives what the app shows you (owner-only controls).
      if (member.userId === currentUserId) qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });

  const isSelf = member.userId === currentUserId;

  return (
    <>
      <div className="py-2.5">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="truncate text-sm text-ink">
              {member.email}
              {isSelf && (
                <span className="ml-1.5 text-2xs text-ink-faint">(you)</span>
              )}
            </p>
            <p className="text-2xs text-ink-faint">joined {formatDate(member.createdAt)}</p>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role={member.role} />
            {canManage && !(member.role === 'owner' && isLastOwner) && (
              <button
                type="button"
                onClick={() => setConfirmingRole(true)}
                disabled={changeRole.isPending}
                className="btn-ghost btn-sm"
                aria-label={`${nextRole === 'owner' ? 'Make owner' : 'Make member'}: ${member.email}`}
              >
                {nextRole === 'owner' ? 'Make owner' : 'Make member'}
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => setGrantsOpen((v) => !v)}
                aria-expanded={grantsOpen}
                className="btn-ghost btn-sm"
                title="Project access"
                aria-label={`Project access for ${member.email}`}
              >
                {grantsOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </button>
            )}
            {canManage && !isSelf && (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={remove.isPending}
                className="btn-ghost btn-sm text-danger"
                title="Remove member"
                aria-label="Remove member"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        {changeRole.isError && (
          <p className="mt-1 text-2xs text-danger">{errorMessage(changeRole.error)}</p>
        )}
        {canManage && grantsOpen && (
          <div className="mt-2 pl-0.5">
            <MemberGrantsEditor userId={member.userId} />
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title="Remove member"
        message={`Remove ${member.email} from this workspace? They will lose access immediately.`}
        confirmLabel="Remove"
        onConfirm={() => {
          setConfirming(false);
          remove.mutate();
        }}
        onClose={() => setConfirming(false)}
      />
      <ConfirmDialog
        open={confirmingRole}
        title={nextRole === 'owner' ? 'Make owner' : 'Make member'}
        message={
          nextRole === 'owner'
            ? `Make ${member.email} an owner? Owners have full control: every project, billing and plan settings, managing members, and deleting the workspace.`
            : isSelf
              ? 'Step down to member? You’ll keep only your per-project access and can no longer manage this workspace.'
              : `Make ${member.email} a member? They keep only their per-project access.`
        }
        confirmLabel={nextRole === 'owner' ? 'Make owner' : 'Make member'}
        onConfirm={() => {
          setConfirmingRole(false);
          changeRole.mutate();
        }}
        onClose={() => setConfirmingRole(false)}
      />
    </>
  );
}

function InviteRow({
  invite,
  canManage,
  orgId,
}: {
  invite: OrgInviteDto;
  canManage: boolean;
  orgId: string;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [resentToken, setResentToken] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: () => revokeOrgInvite(orgId, invite.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) }),
  });

  const resend = useMutation({
    mutationFn: () => resendOrgInvite(orgId, invite.id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) });
      setResentToken(data.token ?? null);
    },
  });

  return (
    <>
      <div className="flex items-center justify-between py-2.5 opacity-70">
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{invite.invitedEmail}</p>
          <p className="text-2xs text-ink-faint">
            invited · expires {formatDate(invite.expiresAt)}
          </p>
          {resentToken && (
            <p className="mt-0.5 text-2xs text-success">
              Invitation resent.{' '}
              <span className="font-mono">(Dev token: {resentToken})</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RoleBadge role={invite.role} />
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => { setResentToken(null); resend.mutate(); }}
                disabled={resend.isPending || revoke.isPending}
                className="btn-ghost btn-sm"
                title="Resend invitation"
                aria-label="Resend invitation"
              >
                <RefreshCw className={`h-3.5 w-3.5${resend.isPending ? ' animate-spin' : ''}`} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={revoke.isPending || resend.isPending}
                className="btn-ghost btn-sm text-danger"
                title="Revoke invitation"
                aria-label="Revoke invitation"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        title="Revoke invitation"
        message={`Revoke the invitation for ${invite.invitedEmail}? They will no longer be able to accept it.`}
        confirmLabel="Revoke"
        onConfirm={() => {
          setConfirming(false);
          revoke.mutate();
        }}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}

function InviteForm({ orgId, onSuccess }: { orgId: string; onSuccess?: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  // Owners hold every capability; members are deny-by-default and get write
  // power only from per-project access (viewer / contributor) granted below.
  const [role, setRole] = useState<'owner' | 'member'>('member');
  // Optional project access granted at invite time, keyed by project id —
  // materializes into real grants once the invite is accepted (see
  // InviteGrantInput). 'none' entries are simply omitted from the request.
  const [projectRoles, setProjectRoles] = useState<Record<string, GrantSelectValue>>({});
  const { options: projectOptions } = useWorkspaceProjectOptions();

  const invite = useMutation({
    mutationFn: () => {
      // Owners see every project — the API rejects grants on an owner invite.
      if (role === 'owner') return inviteOrgMember(orgId, email, 'owner', []);
      const grants: InviteGrantInput[] = Object.entries(projectRoles)
        .filter((entry): entry is [string, ProjectRole] => entry[1] !== 'none')
        .map(([projectId, projectRole]) => ({ projectId, role: projectRole }));
      return inviteOrgMember(orgId, email, 'member', grants);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.orgMembers(orgId) });
      onSuccess?.();
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    invite.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {invite.isError && (
        <Callout tone="danger">
          {errorMessage(invite.error)}
        </Callout>
      )}
      <FormField label="Email address">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@example.com"
          className="input"
        />
      </FormField>
      <FormField
        label="Role"
        hint={
          role === 'owner'
            ? 'Owners have full control: every project, billing and plan settings, managing members, and deleting the workspace.'
            : 'Members see only the projects you give them access to.'
        }
      >
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'owner' | 'member')}
          className="input"
        >
          <option value="member">Member</option>
          <option value="owner">Owner</option>
        </select>
      </FormField>
      {role === 'member' && projectOptions.length > 0 && (
        <div className="border-t border-line pt-3">
          <p className="mb-1 text-xs font-medium text-ink-muted">
            Project access (optional)
          </p>
          <div className="divide-y divide-line">
            {projectOptions.map((project) => (
              <GrantSelect
                key={project.id}
                label={project.name}
                value={projectRoles[project.id] ?? 'none'}
                onChange={(value) =>
                  setProjectRoles((prev) => ({ ...prev, [project.id]: value }))
                }
              />
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end border-t border-line pt-1">
        <button
          type="submit"
          disabled={invite.isPending}
          className="btn-primary"
        >
          <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </form>
  );
}

export function MembersSettingsSection() {
  useDocumentTitle('Settings · Members');
  const { user } = useAuth();
  const currentUserId = user?.id ?? '';
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.id ?? '';
  const isOwner = activeOrg?.role === 'owner';
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: qk.orgMembers(orgId),
    queryFn: () => fetchOrgMembers(orgId),
    enabled: Boolean(orgId),
  });

  if (isLoading) return <LoadingState label="Loading members…" />;
  if (isError) return <ErrorState message="Could not load members" onRetry={() => refetch()} />;

  const members = data?.members ?? [];
  const ownerCount = members.filter((m) => m.role === 'owner').length;
  const invites = data?.invites ?? [];

  return (
    <div className="space-y-6">
      {isOwner && (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              className="btn-primary"
              onClick={() => setInviteOpen(true)}
            >
              <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
              Invite member
            </button>
          </div>
          <Modal
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
            title="Invite member"
            widthClass="max-w-xl"
          >
            <InviteForm orgId={orgId} onSuccess={() => setInviteOpen(false)} />
          </Modal>
        </>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold text-ink">
          Members
          <span className="ml-1.5 font-normal text-ink-faint">({members.length})</span>
        </h2>
        <div className="card divide-y divide-line">
          {members.map((m) => (
            <div key={m.id} className="px-4">
              <MemberRow
                member={m}
                canManage={isOwner}
                currentUserId={currentUserId}
                orgId={orgId}
                isLastOwner={ownerCount <= 1}
              />
            </div>
          ))}
          {members.length === 0 && (
            <p className="px-4 py-3 text-sm text-ink-faint">No members yet.</p>
          )}
        </div>
      </div>

      {invites.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold text-ink">Pending invitations</h2>
          <div className="card divide-y divide-line">
            {invites.map((inv) => (
              <div key={inv.id} className="px-4">
                <InviteRow invite={inv} canManage={isOwner} orgId={orgId} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
